import React, { useState } from 'react';
import { Plus, Trash2, GripVertical, Save } from 'lucide-react';

const FIELD_TYPES = [
  { value: 'text', label: 'Text Input' },
  { value: 'email', label: 'Email' },
  { value: 'tel', label: 'Phone Number' },
  { value: 'number', label: 'Number' },
  { value: 'url', label: 'URL' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'select', label: 'Dropdown' },
  { value: 'radio', label: 'Radio Buttons' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'date', label: 'Date' },
];

const DEFAULT_SETTINGS = {
  successMessage: 'Thank you! Your form has been submitted.',
  submitButtonText: 'Submit',
  redirectUrl: '',
  emailNotifications: true,
  notificationEmail: '',
};

const FormBuilder = ({ initialForm = null, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: initialForm?.name || '',
    description: initialForm?.description || '',
    active: initialForm?.active !== undefined ? initialForm.active : true,
    fields: initialForm?.fields ? JSON.parse(initialForm.fields) : [],
    // Merge over the defaults so forms saved before a setting existed still
    // render as controlled inputs
    settings: {
      ...DEFAULT_SETTINGS,
      ...(initialForm?.settings ? JSON.parse(initialForm.settings) : {}),
    },
  });

  const [editingFieldIndex, setEditingFieldIndex] = useState(null);
  const [errors, setErrors] = useState({});

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleSettingsChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        [field]: value,
      },
    }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const addField = () => {
    const newField = {
      type: 'text',
      label: '',
      name: '',
      placeholder: '',
      required: false,
      options: [],
    };
    setFormData(prev => ({
      ...prev,
      fields: [...prev.fields, newField],
    }));
    setEditingFieldIndex(formData.fields.length);
  };

  const updateField = (index, field, value) => {
    const updatedFields = [...formData.fields];
    updatedFields[index] = {
      ...updatedFields[index],
      [field]: value,
    };
    setFormData(prev => ({
      ...prev,
      fields: updatedFields,
    }));
  };

  const removeField = (index) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index),
    }));
    if (editingFieldIndex === index) {
      setEditingFieldIndex(null);
    }
  };

  const moveField = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= formData.fields.length) return;

    const updatedFields = [...formData.fields];
    const temp = updatedFields[index];
    updatedFields[index] = updatedFields[newIndex];
    updatedFields[newIndex] = temp;

    setFormData(prev => ({
      ...prev,
      fields: updatedFields,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate form
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Form name is required';
    }
    if (formData.fields.length === 0) {
      newErrors.fields = 'At least one field is required';
    }

    // Validate each field
    formData.fields.forEach((field, index) => {
      if (!field.label.trim()) {
        newErrors[`field_${index}_label`] = 'Field label is required';
      }
      if (!field.name.trim()) {
        newErrors[`field_${index}_name`] = 'Field name is required';
      }
    });

    const notificationEmail = formData.settings.notificationEmail.trim();
    if (notificationEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(notificationEmail)) {
      newErrors.notificationEmail = 'Enter a valid email address';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Prepare data for submission
    const submitData = {
      name: formData.name,
      description: formData.description,
      active: formData.active,
      fields: JSON.stringify(formData.fields),
      settings: JSON.stringify({
        ...formData.settings,
        notificationEmail,
      }),
    };

    onSave(submitData);
  };

  const addOption = (fieldIndex) => {
    const updatedFields = [...formData.fields];
    if (!updatedFields[fieldIndex].options) {
      updatedFields[fieldIndex].options = [];
    }
    updatedFields[fieldIndex].options.push('');
    setFormData(prev => ({
      ...prev,
      fields: updatedFields,
    }));
  };

  const updateOption = (fieldIndex, optionIndex, value) => {
    const updatedFields = [...formData.fields];
    updatedFields[fieldIndex].options[optionIndex] = value;
    setFormData(prev => ({
      ...prev,
      fields: updatedFields,
    }));
  };

  const removeOption = (fieldIndex, optionIndex) => {
    const updatedFields = [...formData.fields];
    updatedFields[fieldIndex].options = updatedFields[fieldIndex].options.filter((_, i) => i !== optionIndex);
    setFormData(prev => ({
      ...prev,
      fields: updatedFields,
    }));
  };

  return (
    <div className="max-w-6xl mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Form Details */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Form Details</h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Form Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="e.g., Contact Form, Survey, Registration"
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows="2"
                placeholder="Optional description for your form"
              />
            </div>

            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => handleFormChange('active', e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm font-medium text-gray-700">Active (form is accepting submissions)</span>
              </label>
            </div>
          </div>
        </div>

        {/* Form Fields */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Form Fields</h3>
            <button
              type="button"
              onClick={addField}
              className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Field
            </button>
          </div>

          {errors.fields && <p className="text-red-500 text-sm mb-4">{errors.fields}</p>}

          <div className="space-y-4">
            {formData.fields.map((field, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center">
                    <GripVertical className="w-5 h-5 text-gray-400 mr-2" />
                    <span className="font-medium text-gray-700">
                      Field {index + 1}: {field.label || 'Untitled Field'}
                    </span>
                  </div>
                  <div className="flex space-x-2">
                    {index > 0 && (
                      <button
                        type="button"
                        onClick={() => moveField(index, 'up')}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        ↑
                      </button>
                    )}
                    {index < formData.fields.length - 1 && (
                      <button
                        type="button"
                        onClick={() => moveField(index, 'down')}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        ↓
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => removeField(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Field Type *
                    </label>
                    <select
                      value={field.type}
                      onChange={(e) => updateField(index, 'type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    >
                      {FIELD_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Field Name (identifier) *
                    </label>
                    <input
                      type="text"
                      value={field.name}
                      onChange={(e) => updateField(index, 'name', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                        errors[`field_${index}_name`] ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="e.g., email, phone, message"
                    />
                    {errors[`field_${index}_name`] && (
                      <p className="text-red-500 text-sm mt-1">{errors[`field_${index}_name`]}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Label *
                    </label>
                    <input
                      type="text"
                      value={field.label}
                      onChange={(e) => updateField(index, 'label', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                        errors[`field_${index}_label`] ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="e.g., Email Address, Phone Number"
                    />
                    {errors[`field_${index}_label`] && (
                      <p className="text-red-500 text-sm mt-1">{errors[`field_${index}_label`]}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Placeholder
                    </label>
                    <input
                      type="text"
                      value={field.placeholder || ''}
                      onChange={(e) => updateField(index, 'placeholder', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      placeholder="Optional placeholder text"
                    />
                  </div>

                  <div className="flex items-center">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={field.required || false}
                        onChange={(e) => updateField(index, 'required', e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium text-gray-700">Required field</span>
                    </label>
                  </div>
                </div>

                {/* Options for select, radio */}
                {(field.type === 'select' || field.type === 'radio') && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Options
                    </label>
                    <div className="space-y-2">
                      {(field.options || []).map((option, optionIndex) => (
                        <div key={optionIndex} className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={option}
                            onChange={(e) => updateOption(index, optionIndex, e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                            placeholder={`Option ${optionIndex + 1}`}
                          />
                          <button
                            type="button"
                            onClick={() => removeOption(index, optionIndex)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => addOption(index)}
                        className="text-blue-600 hover:text-blue-700 text-sm"
                      >
                        + Add Option
                      </button>
                    </div>
                  </div>
                )}

                {/* Checkbox label */}
                {field.type === 'checkbox' && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Checkbox Label
                    </label>
                    <input
                      type="text"
                      value={field.checkboxLabel || ''}
                      onChange={(e) => updateField(index, 'checkboxLabel', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., I agree to the terms and conditions"
                    />
                  </div>
                )}
              </div>
            ))}

            {formData.fields.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No fields added yet. Click "Add Field" to create your first form field.
              </div>
            )}
          </div>
        </div>

        {/* Form Settings */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Form Settings</h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Submit Button Text
              </label>
              <input
                type="text"
                value={formData.settings.submitButtonText}
                onChange={(e) => handleSettingsChange('submitButtonText', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="Submit"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Success Message
              </label>
              <textarea
                value={formData.settings.successMessage}
                onChange={(e) => handleSettingsChange('successMessage', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                rows="2"
                placeholder="Thank you! Your form has been submitted."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Redirect URL (Optional)
              </label>
              <input
                type="url"
                value={formData.settings.redirectUrl}
                onChange={(e) => handleSettingsChange('redirectUrl', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="https://example.com/thank-you"
              />
              <p className="text-sm text-gray-500 mt-1">
                Redirect users to this URL after successful submission
              </p>
            </div>
          </div>
        </div>

        {/* Where Submissions Go */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-1">Where Submissions Go</h3>
          <p className="text-sm text-gray-500 mb-4">
            Every submission is stored in VisitorStatus and can be emailed to you as it arrives.
          </p>

          <div className="space-y-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.settings.emailNotifications}
                onChange={(e) => handleSettingsChange('emailNotifications', e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">
                Email me each new submission
              </span>
            </label>

            {formData.settings.emailNotifications && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Send Submissions To (Optional)
                </label>
                <input
                  type="email"
                  value={formData.settings.notificationEmail}
                  onChange={(e) => handleSettingsChange('notificationEmail', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                    errors.notificationEmail ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="sales@example.com"
                />
                {errors.notificationEmail && (
                  <p className="text-sm text-red-600 mt-1">{errors.notificationEmail}</p>
                )}
                <p className="text-sm text-gray-500 mt-1">
                  Leave blank to send submissions to your account email address
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Save className="w-4 h-4 mr-2" />
            Save Form
          </button>
        </div>
      </form>
    </div>
  );
};

export default FormBuilder;
